import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { Customer } from '@/modules/core/data/types/Customer';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { ReactSelectRefLabelOption } from '@/modules/core/reactSelect/types/ReactSelect';
import {
  convertCampusesAsSelectOptions,
  filterCampusesByProgramSteps,
} from '@/modules/solutions/campus';
import { TFunction } from 'i18next';
import map from 'lodash.map';
import size from 'lodash.size';
import { RESPONSE_DISPLAY_TYPE } from '../../chatbot/types/Chatbot';
import { convertStepsToStepsGroupedByProgramsAsSelectOptions } from '../../solutions/educationalProgramStep';

/**
 * All questions that might be asked by the chatbot.
 */
export type ChatbotQuestions = {
  personaQuestion: ChatbotQuestion;
  programsQuestion: ChatbotQuestion;
  programStepsQuestion: ChatbotQuestion;
  campusesQuestion: ChatbotQuestion;
}

/**
 * Compute all questions that might be asked by the chatbot, and their configuration (choices, i18n, etc.)
 */
export const getQuestionsToAsk = (context: ChatbotContext, t: TFunction, customer: Customer, allEducationalPrograms: EducationalProgram[], allEducationalProgramSteps: EducationalProgramStep[], allCampuses: Campus[], allStudentSolutions: StudentSolution[]): any => {
  const personaQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.persona', 'Pour commencer, faisons connaissance ! Vous êtes&nbsp;:'),
    choices: [
      {
        value: 'candidate',
        label: t('chatbotPage.dialog.question.personaChoices.candidate', 'Un candidat'),
      },
      {
        value: 'student',
        label: t('chatbotPage.dialog.question.personaChoices.student', `Un étudiant de <b>{{label}}</b>`, {
          label: customer?.labelShort,
        }),
      },
      {
        value: 'parent',
        label: t('chatbotPage.dialog.question.personaChoices.parent', 'Un parent'),
      },
      {
        value: 'other',
        label: t('chatbotPage.dialog.question.personaChoices.other', 'Autre'),
      },
    ],
    order: 1,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.SINGLE_CHOICE_INPUT,
    ref: 'persona',
    hideIfNoChoice: true,
  };
  const programsQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.educationalProgram', 'Quel est le programme qui vous intéresse ?'),
    choices: map(allEducationalPrograms, (choice): ReactSelectRefLabelOption => {
      return {
        ref: choice.ref,
        label: choice.label,
      };
    }),
    order: 1,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.MULTI_SELECT_INPUT,
    ref: 'educationalPrograms',
    hideIfNoChoice: true,
  };
  const programStepsQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.educationalProgramStep', 'Et plus précisément ...', { count: size(context?.educationalPrograms) }),
    choices: convertStepsToStepsGroupedByProgramsAsSelectOptions(allEducationalProgramSteps, context.educationalPrograms),
    order: 2,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.MULTI_SELECT_INPUT,
    ref: 'educationalProgramSteps',
    hideIfNoChoice: true,
  };
  const campusesQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.campus', 'Dans quel campus souhaitez-vous étudier ?'),
    choices: convertCampusesAsSelectOptions(filterCampusesByProgramSteps(allCampuses, allEducationalProgramSteps, context.educationalProgramSteps)),
    order: 3,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.SINGLE_CHOICE_INPUT,
    ref: 'campuses',
    hideIfNoChoice: true,
  };

  return {
    personaQuestion,
    programsQuestion,
    programStepsQuestion,
    campusesQuestion,
  };
};

export default getQuestionsToAsk;
